import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { SwollenLegsHeroSection } from '../components/SwollenLegsHeroSection';
import { ProblemSection } from '../components/ProblemSection';
import { SolutionSection } from '../components/SolutionSection';
import { MechanismSection } from '../components/MechanismSection';
import { BenefitsSection } from '../components/BenefitsSection';
import { UseCasesSection } from '../components/UseCasesSection';
import { CompareSection } from '../components/CompareSection';
import { HowToUseSection } from '../components/HowToUseSection';
import { TrustSection } from '../components/TrustSection';
import { BundleSection } from '../components/BundleSection';
import { FAQSection } from '../components/FAQSection';
import { CTASection } from '../components/CTASection';
import { PolicyModal } from '../components/PolicyModal';
import { SalesNotification } from '../components/SalesNotification';
import { PageTracker } from '../components/PageTracker';
import { FloatingCTA } from '../components/FloatingCTA';
type PolicyType = 'privacy' | 'terms' | 'refund';
const navigationLinks = [{
  name: 'How It Works',
  href: '#how-it-works'
}, {
  name: 'Benefits',
  href: '#benefits'
}, {
  name: 'How To Use',
  href: '#how-to-use'
}, {
  name: 'Pricing',
  href: '#bundles'
}, {
  name: 'FAQ',
  href: '#faq'
}];
export function SwollenLegsPage() {
  const [policyModal, setPolicyModal] = useState<{
    isOpen: boolean;
    type: PolicyType;
  }>({
    isOpen: false,
    type: 'privacy'
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  // Handle scrolling to hash on mount and when hash changes
  useEffect(() => {
    if (location.hash) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        const element = document.querySelector(location.hash);
        if (element) {
          const yOffset = -80;
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({
            top: y,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [location.hash]);
  const openPolicy = (type: PolicyType) => {
    setPolicyModal({
      isOpen: true,
      type
    });
  };
  const closePolicy = () => {
    setPolicyModal({
      ...policyModal,
      isOpen: false
    });
  };
  const handleNavClick = (href: string) => {
    setMobileMenuOpen(false);
    // Small delay to allow mobile menu to close before scrolling
    setTimeout(() => {
      const element = document.querySelector(href);
      if (element) {
        // Calculate the position accounting for fixed header
        const yOffset = -80;
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({
          top: y,
          behavior: 'smooth'
        });
      }
    }, 100);
  };
  return <main className="w-full min-h-screen bg-bone overflow-x-hidden" data-id="element-3361">
      <PageTracker data-id="element-3362" />
      {/* Header */}
      <header className="bg-white md:bg-white/95 md:backdrop-blur-sm border-b border-bone-dark fixed top-0 left-0 right-0 z-50 shadow-sm" data-id="element-3363">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between" data-id="element-3364">
          <Link to="/" className="flex items-center" data-id="element-3365">
            <img src="https://cdn.magicpatterns.com/uploads/483EwVYaDBT86YSQWmyKfo/GINEXT_LOGO.png" alt="GINEXT" className="h-6 w-auto" loading="eager" data-id="element-3366" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6" data-id="element-3367">
            {navigationLinks.map(link => <a key={link.name} href={link.href} onClick={e => {
            e.preventDefault();
            handleNavClick(link.href);
          }} className="text-sm font-light text-charcoal hover:text-ginger transition-colors cursor-pointer" data-id="element-3368">
                {link.name}
              </a>)}
          </nav>

          {/* Mobile Menu Button */}
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 text-charcoal hover:text-ginger transition-colors" aria-label="Toggle menu" data-id="element-3369">
            {mobileMenuOpen ? <X className="w-6 h-6" data-id="element-3370" /> : <Menu className="w-6 h-6" data-id="element-3371" />}
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        <AnimatePresence data-id="element-3372">
          {mobileMenuOpen && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: [0.16, 1, 0.3, 1]
        }} className="md:hidden overflow-hidden border-t border-bone-dark bg-white" data-id="element-3373">
              <nav className="px-4 py-4 space-y-1" data-id="element-3374">
                {navigationLinks.map(link => <motion.div key={link.name} data-id="element-3375">
                    <a href={link.href} onClick={e => {
                e.preventDefault();
                setMobileMenuOpen(false);
                handleNavClick(link.href);
              }} className="block py-3 px-4 text-base font-light text-charcoal hover:text-ginger hover:bg-bone/50 rounded-lg transition-all cursor-pointer" data-id="element-3376">
                      {link.name}
                    </a>
                  </motion.div>)}
              </nav>
            </motion.div>}
        </AnimatePresence>
      </header>

      {/* Add padding to account for fixed header */}
      <div className="pt-[57px]" data-id="element-3377">
        <SwollenLegsHeroSection data-id="element-3378" />
        <ProblemSection data-id="element-3379" />
        <SolutionSection data-id="element-3380" />
        <div id="how-it-works" className="scroll-mt-20" data-id="element-3381">
          <MechanismSection data-id="element-3382" />
        </div>
        <div id="benefits" className="scroll-mt-20" data-id="element-3383">
          <BenefitsSection beforeImage="https://cdn.magicpatterns.com/uploads/6CcWJy3w5feuNNFsbabayV/feets_.png" afterImage="https://cdn.magicpatterns.com/uploads/6aNW92ThfkGsG5cB4vdddM/after.png" data-id="element-3384" />
        </div>
        <CompareSection data-id="element-3385" />
        <UseCasesSection data-id="element-3386" />
        <div id="how-to-use" className="scroll-mt-20" data-id="element-3387">
          <HowToUseSection data-id="element-3388" />
        </div>
        <TrustSection data-id="element-3389" />
        <div id="bundles" className="scroll-mt-20" data-id="element-3390">
          <BundleSection data-id="element-3391" />
        </div>
        <div id="faq" className="scroll-mt-20" data-id="element-3392">
          <FAQSection data-id="element-3393" />
        </div>
        <CTASection data-id="element-3394" />
      </div>

      {/* Footer */}
      <footer className="bg-charcoal text-gray-400 py-4 text-center text-xs border-t border-white/10" data-id="element-3395">
        <div className="max-w-6xl mx-auto px-4" data-id="element-3396">
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-3" data-id="element-3397">
            <button onClick={() => openPolicy('privacy')} className="hover:text-ginger transition-colors underline" data-id="element-3398">
              Privacy Policy
            </button>
            <button onClick={() => openPolicy('terms')} className="hover:text-ginger transition-colors underline" data-id="element-3399">
              Terms of Service
            </button>
            <button onClick={() => openPolicy('refund')} className="hover:text-ginger transition-colors underline" data-id="element-3400">
              Refund Policy
            </button>
          </div>
          <p data-id="element-3401">© {new Date().getFullYear()} GINEXT. All rights reserved.</p>
          <p className="mt-1" data-id="element-3402">
            These statements have not been evaluated by the Food and Drug
            Administration. This product is not intended to diagnose, treat,
            cure, or prevent any disease.
          </p>
        </div>
      </footer>

      <PolicyModal isOpen={policyModal.isOpen} onClose={closePolicy} type={policyModal.type} data-id="element-3403" />

      {/* Sales Notification */}
      <SalesNotification data-id="element-3404" />

      {/* Floating CTA - Mobile & Desktop */}
      <FloatingCTA ctaText="Try GINEXT Risk-Free" ctaLink="#bundles" subText="30-Day Money Back Guarantee" data-id="element-3405" />
    </main>;
}